import React from 'react';
import { DayAvailabilityRow, type TimeRange } from './DayAvailabilityRow';
import { v4 as uuidv4 } from 'uuid';

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
  { value: 0, label: 'Sunday', short: 'Sun' },
];

export interface DaySchedule {
  day_of_week: number;
  is_available: boolean;
  time_ranges: TimeRange[];
}

interface WeeklyAvailabilityProps {
  schedule: DaySchedule[];
  onChange: (schedule: DaySchedule[]) => void;
  disabled?: boolean;
}

export const WeeklyAvailability: React.FC<WeeklyAvailabilityProps> = ({
  schedule,
  onChange,
  disabled = false,
}) => {
  const getScheduleForDay = (dayOfWeek: number): DaySchedule => {
    const existing = schedule.find((s) => s.day_of_week === dayOfWeek);
    if (existing) return existing;
    return {
      day_of_week: dayOfWeek,
      is_available: false,
      time_ranges: [{ id: uuidv4(), start_time: '09:00:00', end_time: '17:00:00' }],
    };
  };

  const updateDay = (dayOfWeek: number, updates: Partial<DaySchedule>) => {
    const existingIndex = schedule.findIndex((s) => s.day_of_week === dayOfWeek);
    const currentDay = getScheduleForDay(dayOfWeek);
    const updatedDay = { ...currentDay, ...updates };

    if (existingIndex >= 0) {
      const newSchedule = [...schedule];
      newSchedule[existingIndex] = updatedDay;
      onChange(newSchedule);
    } else {
      onChange([...schedule, updatedDay]);
    }
  };

  const handleToggle = (dayOfWeek: number, isAvailable: boolean) => {
    const currentDay = getScheduleForDay(dayOfWeek);
    updateDay(dayOfWeek, {
      is_available: isAvailable,
      time_ranges: currentDay.time_ranges.length === 0
        ? [{ id: uuidv4(), start_time: '09:00:00', end_time: '17:00:00' }]
        : currentDay.time_ranges,
    });
  };

  const handleAddTimeRange = (dayOfWeek: number) => {
    const currentDay = getScheduleForDay(dayOfWeek);
    const lastRange = currentDay.time_ranges[currentDay.time_ranges.length - 1];
    const newRange: TimeRange = {
      id: uuidv4(),
      start_time: lastRange?.end_time || '13:00:00',
      end_time: '17:00:00',
    };
    updateDay(dayOfWeek, {
      time_ranges: [...currentDay.time_ranges, newRange],
    });
  };

  const handleRemoveTimeRange = (dayOfWeek: number, rangeId: string) => {
    const currentDay = getScheduleForDay(dayOfWeek);
    updateDay(dayOfWeek, {
      time_ranges: currentDay.time_ranges.filter((r) => r.id !== rangeId),
    });
  };

  const handleTimeRangeChange = (
    dayOfWeek: number,
    rangeId: string,
    field: 'start_time' | 'end_time',
    value: string
  ) => {
    const currentDay = getScheduleForDay(dayOfWeek);
    updateDay(dayOfWeek, {
      time_ranges: currentDay.time_ranges.map((r) =>
        r.id === rangeId ? { ...r, [field]: value } : r
      ),
    });
  };

  return (
    <div className="space-y-2">
      {DAYS_OF_WEEK.map((day) => {
        const daySchedule = getScheduleForDay(day.value);
        return (
          <DayAvailabilityRow
            key={day.value}
            dayLabel={day.label}
            dayShort={day.short}
            isAvailable={daySchedule.is_available}
            timeRanges={daySchedule.time_ranges}
            onToggle={(available) => handleToggle(day.value, available)}
            onAddTimeRange={() => handleAddTimeRange(day.value)}
            onRemoveTimeRange={(id) => handleRemoveTimeRange(day.value, id)}
            onTimeRangeChange={(id, field, value) =>
              handleTimeRangeChange(day.value, id, field, value)
            }
            disabled={disabled}
          />
        );
      })}
    </div>
  );
};

export default WeeklyAvailability;







